// 관리자 카탈로그 API 호출 래퍼.
// 서버 응답 envelope({ ok, ... }) 형태에서 도메인 필드만 꺼내 반환한다.
// 모든 호출은 api.* 를 거치므로 401 자동 refresh 가 적용된다.

import type {
  BannerCreateInput,
  BannerDto,
  BannerReorderInput,
  BannerUpdateInput,
  CategoryCreateInput,
  CategoryDto,
  CategoryReorderInput,
  CategoryUpdateInput,
  ImageUploadKind,
  ProductBulkPatchInput,
  ProductCreateInput,
  ProductDto,
  ProductReorderInput,
  ProductUpdateInput,
  UnitCreateInput,
  UnitDto,
  UnitUpdateInput,
} from "@oo/shared";
import { api } from "@/api/client.ts";

// 단위
// 전체 단위 목록(비활성 포함). 관리자 단위 관리 화면 및 상품 단위 라벨 표시에 사용.
export async function listUnitsAdmin(): Promise<UnitDto[]> {
  const data = await api.get<{ units: UnitDto[] }>("/admin/units");
  return data.units;
}

// 단위 생성.
export async function createUnit(input: UnitCreateInput): Promise<UnitDto> {
  const data = await api.post<{ unit: UnitDto }>("/admin/units", input);
  return data.unit;
}

// 단위 수정(코드 불변). 없으면 null.
export async function updateUnit(code: string, input: UnitUpdateInput): Promise<UnitDto | null> {
  const data = await api.patch<{ unit: UnitDto | null }>(`/admin/units/${code}`, input);
  return data.unit;
}

// 단위 삭제(사용 중이면 409).
export async function deleteUnit(code: string): Promise<void> {
  await api.delete(`/admin/units/${code}`);
}

// 카테고리
export async function listCategoriesAdmin(): Promise<CategoryDto[]> {
  const data = await api.get<{ categories: CategoryDto[] }>("/admin/categories");
  return data.categories;
}

// 카테고리 생성.
export async function createCategory(input: CategoryCreateInput): Promise<CategoryDto> {
  const data = await api.post<{ category: CategoryDto }>("/admin/categories", input);
  return data.category;
}

// 카테고리 수정. 없으면 null.
export async function updateCategory(id: string, input: CategoryUpdateInput): Promise<CategoryDto | null> {
  const data = await api.patch<{ category: CategoryDto | null }>(`/admin/categories/${id}`, input);
  return data.category;
}

// 카테고리 노출 정렬 순서를 일괄 변경.
export async function reorderCategories(input: CategoryReorderInput): Promise<void> {
  await api.patch("/admin/categories/reorder", input);
}

// 카테고리 삭제.
export async function deleteCategory(id: string): Promise<void> {
  await api.delete(`/admin/categories/${id}`);
}

// 상품
export interface ListProductsResult {
  products: ProductDto[];
  total: number;
  limit: number;
  offset: number;
}

// 관리자 상품 목록. 카테고리/검색어/페이지네이션 파라미터를 쿼리스트링으로 전달.
export async function listProductsAdmin(params: {
  categoryId?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<ListProductsResult> {
  const search = new URLSearchParams();
  if (params.categoryId) search.set("categoryId", params.categoryId);
  if (params.q) search.set("q", params.q);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  return api.get<ListProductsResult>(`/admin/products${qs ? `?${qs}` : ""}`);
}

// 관리자 상품 단건 조회 (숨김/품절 포함).
export async function getProductAdmin(id: string): Promise<ProductDto> {
  const data = await api.get<{ product: ProductDto }>(`/admin/products/${id}`);
  return data.product;
}

// 상품 생성.
export async function createProduct(input: ProductCreateInput): Promise<ProductDto> {
  const data = await api.post<{ product: ProductDto }>("/admin/products", input);
  return data.product;
}

// 상품 수정. 없으면 null.
export async function updateProduct(id: string, input: ProductUpdateInput): Promise<ProductDto | null> {
  const data = await api.patch<{ product: ProductDto | null }>(`/admin/products/${id}`, input);
  return data.product;
}

// 선택 상품 일괄 패치 (품절/노출 토글 등). affected = 영향받은 행 수.
export async function bulkPatchProducts(input: ProductBulkPatchInput): Promise<{ affected: number }> {
  return api.patch<{ affected: number }>("/admin/products/bulk", input);
}

// 상품 삭제 (서버는 소프트 삭제 처리).
export async function deleteProduct(id: string): Promise<void> {
  await api.delete(`/admin/products/${id}`);
}

// 진열관리: 베스트 상품 목록(숨김/품절 포함, best 진열 순서).
export async function listBestProductsAdmin(): Promise<ProductDto[]> {
  const data = await api.get<{ products: ProductDto[] }>("/admin/products/best");
  return data.products;
}

// 진열 순서 일괄 변경(베스트/전체/카테고리). orderedIds 배열 순서가 곧 진열 순서.
export async function reorderProducts(input: ProductReorderInput): Promise<void> {
  await api.patch("/admin/products/reorder", input);
}

// 배너
export async function listBannersAdmin(): Promise<BannerDto[]> {
  const data = await api.get<{ banners: BannerDto[] }>("/admin/banners");
  return data.banners;
}

// 배너 생성.
export async function createBanner(input: BannerCreateInput): Promise<BannerDto> {
  const data = await api.post<{ banner: BannerDto }>("/admin/banners", input);
  return data.banner;
}

// 배너 수정. 없으면 null.
export async function updateBanner(id: string, input: BannerUpdateInput): Promise<BannerDto | null> {
  const data = await api.patch<{ banner: BannerDto | null }>(`/admin/banners/${id}`, input);
  return data.banner;
}

// 배너 노출 순서 일괄 변경. orderedIds 배열 순서가 곧 노출 순서.
export async function reorderBanners(input: BannerReorderInput): Promise<void> {
  await api.patch("/admin/banners/reorder", input);
}

// 배너 삭제.
export async function deleteBanner(id: string): Promise<void> {
  await api.delete(`/admin/banners/${id}`);
}

// 이미지 업로드 (multipart). 본 함수는 api.* 래퍼가 JSON 만 다루므로 fetch 를 직접 쓴다.
// 401 자동 refresh 는 본 흐름엔 적용하지 않는다 - 업로드 실패 시 사용자가 재시도하도록 단순화한다.
export interface UploadedImage {
  key: string;
  contentType: string;
  size: number;
}

// 이미지 업로드 실행. multipart 전송 후 R2 키를 반환한다.
export async function uploadImage(file: File, kind: ImageUploadKind): Promise<UploadedImage> {
  const form = new FormData();
  form.set("kind", kind);
  form.set("file", file);
  const { useAdminAuthStore } = await import("@/stores/adminAuthStore.ts");
  const token = useAdminAuthStore.getState().session?.accessToken;
  const res = await fetch(`${import.meta.env.VITE_API_BASE}/admin/images`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  const data = (await res.json().catch(() => null)) as
    | { ok: true; image: UploadedImage }
    | { ok: false; code: string; message: string }
    | null;
  if (!res.ok || !data || data.ok !== true) {
    const message = data && data.ok === false ? data.message : "이미지 업로드에 실패했습니다.";
    throw new Error(message);
  }
  return data.image;
}
